import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type WheelEvent,
} from 'react';
import type { SeatMap, SeatMapSeat } from '@loctopus/ticketing-sdk';
import { Button, SegmentedToggle } from './ui';

const MIN_SCALE = 0.5;
const MAX_SCALE = 4;

interface Viewport {
  x: number;
  y: number;
  scale: number;
}

function computeBounds(seatMap: SeatMap): {
  minX: number;
  minY: number;
  width: number;
  height: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const section of seatMap.sections) {
    for (const seat of section.seats) {
      if (seat.x === null || seat.y === null) continue;
      minX = Math.min(minX, seat.x);
      minY = Math.min(minY, seat.y);
      maxX = Math.max(maxX, seat.x);
      maxY = Math.max(maxY, seat.y);
    }
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, width: 600, height: 400 };
  const pad = 40;
  return {
    minX: minX - pad,
    minY: minY - pad,
    width: maxX - minX + pad * 2,
    height: maxY - minY + pad * 2,
  };
}

const statusFill: Record<SeatMapSeat['status'], string> = {
  available: '#ffffff',
  held: '#fbbf24',
  sold: '#cbd5e1',
  disabled: '#e2e8f0',
};

export function SeatMapPicker({
  seatMap,
  selectedSeatIds,
  maxSelectable,
  onToggleSeat,
}: {
  seatMap: SeatMap;
  selectedSeatIds: Set<string>;
  maxSelectable: number;
  onToggleSeat: (seat: SeatMapSeat) => void;
}) {
  const [view, setView] = useState<'map' | 'list'>('map');
  const bounds = useMemo(() => computeBounds(seatMap), [seatMap]);
  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, scale: 1 });
  const dragState = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const pinchState = useRef<{ distance: number; scale: number } | null>(null);
  const activePointers = useRef(new Map<number, { x: number; y: number }>());

  const canSelectMore = selectedSeatIds.size < maxSelectable;

  const handleSeatActivate = useCallback(
    (seat: SeatMapSeat) => {
      if (seat.status === 'sold' || seat.status === 'disabled') return;
      const isHeldByOthers = seat.status === 'held' && !selectedSeatIds.has(seat.id);
      if (isHeldByOthers) return;
      if (!selectedSeatIds.has(seat.id) && !canSelectMore) return;
      onToggleSeat(seat);
    },
    [canSelectMore, onToggleSeat, selectedSeatIds],
  );

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.current.size === 1) {
      dragState.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originX: viewport.x,
        originY: viewport.y,
      };
    } else if (activePointers.current.size === 2) {
      const [a, b] = [...activePointers.current.values()];
      if (a && b) {
        pinchState.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: viewport.scale };
      }
    }
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!activePointers.current.has(e.pointerId)) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.current.size === 2 && pinchState.current) {
      const [a, b] = [...activePointers.current.values()];
      if (a && b) {
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const nextScale = clamp(
          (distance / pinchState.current.distance) * pinchState.current.scale,
          MIN_SCALE,
          MAX_SCALE,
        );
        setViewport((v) => ({ ...v, scale: nextScale }));
      }
      return;
    }

    const drag = dragState.current;
    if (drag && drag.pointerId === e.pointerId) {
      const dx = (e.clientX - drag.startX) / viewport.scale;
      const dy = (e.clientY - drag.startY) / viewport.scale;
      setViewport((v) => ({ ...v, x: drag.originX - dx, y: drag.originY - dy }));
    }
  };

  const endPointer = (e: ReactPointerEvent<SVGSVGElement>) => {
    activePointers.current.delete(e.pointerId);
    if (dragState.current?.pointerId === e.pointerId) dragState.current = null;
    if (activePointers.current.size < 2) pinchState.current = null;
  };

  const onWheel = (e: WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.001;
    setViewport((v) => ({ ...v, scale: clamp(v.scale + delta * v.scale, MIN_SCALE, MAX_SCALE) }));
  };

  const zoomBy = (factor: number) =>
    setViewport((v) => ({ ...v, scale: clamp(v.scale * factor, MIN_SCALE, MAX_SCALE) }));
  const resetView = () => setViewport({ x: 0, y: 0, scale: 1 });

  const viewBoxWidth = bounds.width / viewport.scale;
  const viewBoxHeight = bounds.height / viewport.scale;
  const viewBoxX = bounds.minX + bounds.width / 2 - viewBoxWidth / 2 + viewport.x;
  const viewBoxY = bounds.minY + bounds.height / 2 - viewBoxHeight / 2 + viewport.y;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <SegmentedToggle
          label="Seat picker view"
          value={view}
          onChange={setView}
          options={[
            { value: 'map', label: 'Map view' },
            { value: 'list', label: 'List view' },
          ]}
        />
        <Legend />
      </div>

      {view === 'map' ? (
        <div className="relative border border-slate-200 rounded-brand overflow-hidden bg-slate-50 touch-none">
          <svg
            role="group"
            aria-label="Seat map. Pinch or scroll to zoom, drag to pan. Use list view for a keyboard-accessible alternative."
            viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
            className="w-full h-[420px] cursor-grab active:cursor-grabbing"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onWheel={onWheel}
          >
            {seatMap.sections.map((section) => (
              <g key={section.id}>
                {section.seats.map((seat) => {
                  if (seat.x === null || seat.y === null) return null;
                  const selected = selectedSeatIds.has(seat.id);
                  const disabledSeat = seat.status === 'sold' || seat.status === 'disabled';
                  const unavailableForMe = seat.status === 'held' && !selected;
                  return (
                    <circle
                      key={seat.id}
                      cx={seat.x}
                      cy={seat.y}
                      r={9}
                      fill={selected ? 'var(--brand-accent, #7b5ea7)' : statusFill[seat.status]}
                      stroke={disabledSeat || unavailableForMe ? '#94a3b8' : '#1f2937'}
                      strokeWidth={1}
                      opacity={disabledSeat || unavailableForMe ? 0.5 : 1}
                      className={disabledSeat || unavailableForMe ? '' : 'cursor-pointer'}
                      onClick={() => handleSeatActivate(seat)}
                    >
                      <title>
                        {section.name} row {seat.rowLabel} seat {seat.seatNumber} - {seat.status}
                      </title>
                    </circle>
                  );
                })}
              </g>
            ))}
          </svg>
          <div className="absolute bottom-3 right-3 flex flex-col gap-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-label="Zoom in"
              onClick={() => zoomBy(1.3)}
            >
              +
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-label="Zoom out"
              onClick={() => zoomBy(1 / 1.3)}
            >
              -
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-label="Reset view"
              onClick={resetView}
            >
              ⟲
            </Button>
          </div>
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto border border-slate-200 rounded-brand divide-y divide-slate-100">
          {seatMap.sections.map((section) => (
            <div key={section.id} className="p-3">
              <h4 className="font-heading text-sm font-semibold text-slate-700 mb-2">
                {section.name}
                {section.isAccessible && <Badge>Accessible</Badge>}
              </h4>
              <div className="flex flex-wrap gap-2">
                {section.seats.map((seat) => {
                  const selected = selectedSeatIds.has(seat.id);
                  const disabledSeat = seat.status === 'sold' || seat.status === 'disabled';
                  const unavailableForMe = seat.status === 'held' && !selected;
                  return (
                    <button
                      key={seat.id}
                      type="button"
                      disabled={disabledSeat || unavailableForMe}
                      aria-pressed={selected}
                      onClick={() => handleSeatActivate(seat)}
                      className={`px-3 py-1.5 text-sm rounded-brand border focus-ring ${
                        selected
                          ? 'bg-brand-accent text-white border-brand-accent'
                          : 'bg-white border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed'
                      }`}
                    >
                      Row {seat.rowLabel} · Seat {seat.seatNumber}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-xs text-slate-500">
      <LegendDot color={statusFill.available} label="Available" />
      <LegendDot color="var(--brand-accent, #7b5ea7)" label="Selected" />
      <LegendDot color={statusFill.held} label="Held" />
      <LegendDot color={statusFill.sold} label="Sold" />
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="h-2.5 w-2.5 rounded-full border border-slate-400 inline-block"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="ml-2 text-[10px] uppercase tracking-wide text-brand-accent">{children}</span>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
